import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cake, CheckCircle, ArrowLeft, Copy, Phone, MapPin, Calendar, Clock, CreditCard, Banknote } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        setOrder(response.data);
      } catch (error) {
        toast.error("Failed to load order details");
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  const copyOrderId = () => {
    navigator.clipboard.writeText(orderId);
    toast.success("Order ID copied to clipboard");
  };

  const calculateItemsTotal = () => {
    if (!order?.items) return 0;
    return order.items.reduce((total, item) => total + (item.cake_price * item.quantity), 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading order details...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-muted-foreground mb-4">Order not found</p>
        <Link to="/">
          <Button>Return Home</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Cake className="w-10 h-10 text-primary" />
            <span className="font-heading text-3xl font-semibold text-foreground tracking-tight">Sis Carol</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </nav>

      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Success Header */}
          <div className="text-center mb-12 animate-fade-in-up">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl text-foreground mb-4">Order Confirmed!</h1>
            <p className="text-muted-foreground text-lg">Thank you for your order. We'll prepare your cakes with love.</p>
          </div>

          {/* Order ID */}
          <Card className="p-6 rounded-2xl mb-8 animate-fade-in-up animation-delay-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Order ID</p>
                <p className="font-mono text-lg" data-testid="order-id">{orderId}</p>
              </div>
              <Button variant="outline" size="sm" onClick={copyOrderId} data-testid="copy-order-id-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy
              </Button>
            </div>
          </Card>

          {/* Order Details */}
          <Card className="p-6 md:p-8 rounded-2xl mb-8 animate-fade-in-up animation-delay-200">
            <h2 className="font-heading text-2xl mb-6">Order Details</h2>
            
            <div className="space-y-6">
              {/* Items List */}
              <div className="pb-6 border-b border-border">
                <h3 className="font-medium flex items-center gap-2 mb-4">
                  <Cake className="w-4 h-4 text-primary" /> Items Ordered ({order.items?.length || 0})
                </h3>
                <div className="space-y-4">
                  {order.items?.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 bg-secondary/30 p-4 rounded-xl">
                      <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Cake className="w-8 h-8 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-heading font-semibold">{item.cake_name}</h4>
                        <p className="text-sm text-muted-foreground">Quantity: {item.quantity}</p>
                        {item.cake_message && (
                          <p className="text-sm text-muted-foreground">Message: "{item.cake_message}"</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">J${item.cake_price.toLocaleString()} each</p>
                        <p className="font-heading text-primary">J${(item.cake_price * item.quantity).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Customer Details */}
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary" /> Contact Details
                </h3>
                <div className="pl-6 space-y-1 text-sm">
                  <p>{order.customer_name}</p>
                  <p className="text-muted-foreground">{order.phone}</p>
                  {order.email && <p className="text-muted-foreground">{order.email}</p>}
                </div>
              </div>

              {/* Delivery Details */}
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" /> Delivery Address
                </h3>
                <div className="pl-6 text-sm text-muted-foreground">
                  <p>{order.address}</p>
                  <p className="mt-1">{order.delivery_zone_name}</p>
                </div>
              </div>

              {/* Date & Time */}
              <div className="flex gap-8">
                <div className="space-y-2">
                  <h3 className="font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" /> Delivery Date
                  </h3>
                  <p className="pl-6 text-sm text-muted-foreground">{order.delivery_date}</p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4 text-primary" /> Delivery Time
                  </h3>
                  <p className="pl-6 text-sm text-muted-foreground">{order.delivery_time}</p>
                </div>
              </div>

              {/* Payment Method */}
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  {order.payment_method === "bank_transfer" ? (
                    <CreditCard className="w-4 h-4 text-primary" />
                  ) : (
                    <Banknote className="w-4 h-4 text-primary" />
                  )}
                  Payment Method
                </h3>
                <p className="pl-6 text-sm text-muted-foreground">
                  {order.payment_method === "bank_transfer" ? "Bank Transfer (Pay Before Delivery)" : "Cash on Delivery/Pickup"}
                </p>
              </div>

              {/* Payment Instructions */}
              <div className="bg-green-50 border border-green-200 p-4 rounded-xl">
                <p className="font-medium text-green-800">Payment Due: J${order.total_amount.toLocaleString()}</p>
                {order.payment_method === "cash" ? (
                  <p className="text-sm text-green-700 mt-1">
                    Please have exact cash ready when your cake arrives or when you pick up.
                  </p>
                ) : (
                  <div className="text-sm text-green-700 mt-2">
                    <p className="mb-2">Transfer to:</p>
                    <p>Bank: NCB Jamaica</p>
                    <p>Account Name: Carol McGregor-Dillon</p>
                    <p>Account Number: 584629851</p>
                    <p className="mt-2 font-medium">Include Order ID as reference: {order.id.slice(0, 8)}</p>
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="pt-6 border-t border-border">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Subtotal ({order.items?.length || 0} items)</p>
                    <p className="text-sm text-muted-foreground">Delivery Fee</p>
                    <p className="font-heading text-lg mt-2">Total</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm">J${calculateItemsTotal().toLocaleString()}</p>
                    <p className="text-sm">{order.delivery_fee === 0 ? "Free" : `J$${order.delivery_fee.toLocaleString()}`}</p>
                    <p className="font-heading text-2xl text-primary mt-2" data-testid="confirmation-total">J${order.total_amount.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Status */}
          <Card className="p-6 rounded-2xl mb-8 animate-fade-in-up animation-delay-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Order Status</p>
                <p className="font-heading text-lg capitalize" data-testid="order-status">{order.status}</p>
              </div>
              <span className={`px-4 py-2 rounded-full text-sm font-medium status-${order.status}`}>
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-400">
            <Link to="/">
              <Button variant="outline" className="rounded-full px-8 w-full sm:w-auto">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
              </Button>
            </Link>
            <Link to="/order">
              <Button className="rounded-full px-8 bg-primary text-primary-foreground w-full sm:w-auto">
                Order More Cakes
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OrderConfirmation;
